import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Signal,
  computed,
  signal,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HicapsConnectV2Routing } from '@principle-theorem/ng-payments';
import { NgDialogModule, TypedFormControl } from '@principle-theorem/ng-shared';
import { HicapsConnectMerchant } from '@principle-theorem/principle-core';
import {
  BridgeDeviceStatus,
  IBridgeDevice,
  IHicapsConnectBridgeEdge,
  IHicapsConnectMerchant,
  IHicapsConnectTerminalV2,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  FieldValue,
  Firestore,
  HISTORY_DATE_FORMAT,
  PartialWithFieldValue,
  WithRef,
  isSameRef,
} from '@principle-theorem/shared';
import { mapValues } from 'lodash';
import { HICAPS_SETTINGS_NOTICE_MS } from '../hicaps-connect-v2-settings-notice';
import { ITerminalWithMerchants } from '../terminal-with-merchants';

export interface IHicapsV2EditTerminalRequest {
  terminalWithMerchants: ITerminalWithMerchants;
  bridges: WithRef<IBridgeDevice>[];
  merchants: WithRef<IHicapsConnectMerchant>[];
}

export interface IHicapsV2BridgeRow {
  bridgeId: string;
  edge: IHicapsConnectBridgeEdge;
  bridgeName: string;
  isBridgeActive: boolean;
  bridgeStatusTooltip: string;
  connectionTooltip: string;
}

export interface IHicapsV2PairRow {
  merchantId: string;
  merchantDisplayName: string;
  enabled: boolean;
  bridges: IHicapsV2BridgeRow[];
}

@Component({
  selector: 'pr-hicaps-connect-v2-edit-terminal-dialog',
  templateUrl: './hicaps-connect-v2-edit-terminal-dialog.component.html',
  styleUrls: ['./hicaps-connect-v2-edit-terminal-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgDialogModule,
    MatSlideToggleModule,
    MatTableModule,
    MatTooltipModule,
  ],
})
export class HicapsConnectV2EditTerminalDialogComponent {
  private readonly _disabledMerchants = signal<Record<string, boolean>>({});
  private readonly _removedBridgeIds = signal<string[]>([]);

  readonly dateFormat = HISTORY_DATE_FORMAT;
  readonly nameControl: TypedFormControl<string>;
  readonly preferredBridgeControl: TypedFormControl<
    DocumentReference<IBridgeDevice> | undefined
  >;
  readonly pairRows: Signal<IHicapsV2PairRow[]>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IHicapsV2EditTerminalRequest,
    private _dialogRef: MatDialogRef<HicapsConnectV2EditTerminalDialogComponent>,
    private _snackBar: MatSnackBar
  ) {
    this.nameControl = new TypedFormControl<string>(
      data.terminalWithMerchants.name,
      Validators.required
    );
    this.preferredBridgeControl = new TypedFormControl<
      DocumentReference<IBridgeDevice> | undefined
    >(data.terminalWithMerchants.preferredBridge);
    this.pairRows = computed(() =>
      this._buildPairRows(this._disabledMerchants(), this._removedBridgeIds())
    );
  }

  readonly compareWithBridge = (
    selected: DocumentReference<IBridgeDevice> | undefined,
    option: DocumentReference<IBridgeDevice> | undefined
  ): boolean => {
    if (!selected || !option) {
      return selected === option;
    }
    return isSameRef(selected, option);
  };

  readonly getAutomaticBridgeLabel = (
    terminalWithMerchants: ITerminalWithMerchants
  ): string =>
    terminalWithMerchants.servingBridge
      ? `Automatic (${terminalWithMerchants.servingBridge})`
      : 'Automatic';

  merchantEnabledChanged(merchantId: string, enabled: boolean): void {
    this._disabledMerchants.update((current) => ({
      ...current,
      [merchantId]: !enabled,
    }));
  }

  removeBridge(bridgeId: string): void {
    this._removedBridgeIds.update((current) =>
      current.includes(bridgeId) ? current : [...current, bridgeId]
    );
  }

  async save(): Promise<void> {
    const terminalRef = this.data.terminalWithMerchants.terminal.ref;

    try {
      await Firestore.patchDoc(terminalRef, this._buildTerminalPatch());
    } catch {
      this._snackBar.open('Save failed', 'OK', {
        duration: HICAPS_SETTINGS_NOTICE_MS,
      });
      return;
    }

    this._dialogRef.close();
  }

  /**
   * Only the fields the dialog owns are written. The bridge and merchant maps
   * are addressed key by key rather than rewritten wholesale, so a bridge
   * heartbeat landing while the dialog is open is not rolled back by the save.
   */
  private _buildTerminalPatch(): PartialWithFieldValue<IHicapsConnectTerminalV2> {
    const preferredBridge = this.preferredBridgeControl.value;

    return {
      name: this.nameControl.value,
      preferredBridge: preferredBridge ?? FieldValue.delete(),
      bridges: this._removedBridgesPatch(),
      merchants: this._merchantEdgesPatch(),
    };
  }

  private _removedBridgesPatch(): Record<string, FieldValue> {
    return Object.fromEntries(
      this._removedBridgeIds().map((bridgeId) => [
        bridgeId,
        FieldValue.delete(),
      ])
    );
  }

  private _merchantEdgesPatch(): Record<string, { disabled: boolean }> {
    return mapValues(this._disabledMerchants(), (disabled) => ({ disabled }));
  }

  private _buildPairRows(
    disabledMerchants: Record<string, boolean>,
    removedBridgeIds: string[]
  ): IHicapsV2PairRow[] {
    const terminal = this.data.terminalWithMerchants.terminal;

    return Object.entries(terminal.merchants).map(([merchantId, edge]) => ({
      merchantId,
      merchantDisplayName: this._merchantDisplayName(merchantId),
      enabled: !(disabledMerchants[merchantId] ?? edge.disabled),
      bridges: this._buildBridgeRows(terminal, removedBridgeIds),
    }));
  }

  private _buildBridgeRows(
    terminal: WithRef<IHicapsConnectTerminalV2>,
    removedBridgeIds: string[]
  ): IHicapsV2BridgeRow[] {
    return Object.entries(terminal.bridges)
      .filter(([bridgeId]) => !removedBridgeIds.includes(bridgeId))
      .map(([bridgeId, edge]) => ({
        bridgeId,
        edge,
        bridgeName: this._bridgeName(bridgeId),
        isBridgeActive: this._isBridgeActive(bridgeId),
        bridgeStatusTooltip: this._bridgeStatusTooltip(bridgeId),
        connectionTooltip: this._connectionTooltip(edge),
      }));
  }

  private _bridgeStatus(bridgeId: string): BridgeDeviceStatus | undefined {
    return HicapsConnectV2Routing.findBridgeDevice(this.data.bridges, bridgeId)
      ?.status;
  }

  private _isBridgeActive(bridgeId: string): boolean {
    return this._bridgeStatus(bridgeId) === BridgeDeviceStatus.Active;
  }

  private _bridgeStatusTooltip(bridgeId: string): string {
    const status = this._bridgeStatus(bridgeId);
    if (!status) {
      return 'Bridge device not found';
    }
    return status === BridgeDeviceStatus.Active
      ? 'Bridge device is active'
      : 'Bridge device is inactive';
  }

  private _connectionTooltip(edge: IHicapsConnectBridgeEdge): string {
    const connection = edge.isLocal
      ? 'Serial: the terminal is plugged directly into this bridge device.'
      : 'Network: this bridge device reaches the terminal over the local network.';
    return `${connection}\nServer URL: ${edge.serverUrl}`;
  }

  private _merchantDisplayName(merchantId: string): string {
    const merchant = this.data.merchants.find(
      (item) => item.merchantId === merchantId
    );

    return HicapsConnectMerchant.displayName(merchant, merchantId);
  }

  private _bridgeName(bridgeId: string): string {
    const bridge = HicapsConnectV2Routing.findBridgeDevice(
      this.data.bridges,
      bridgeId
    );
    return bridge?.name ?? bridgeId;
  }
}
